import axios from 'axios';
import {
  cloneDeep,
  findLast,
  forEach,
  head,
  isEmpty,
  isNumber,
  isString,
  map,
  values,
  merge,
  isPlainObject,
  find,
  some
} from 'lodash-es';
import { isFailedPayment, parseCustomerToRequest } from 'utilities';
import { parseCardBrand } from 'utilities/card';
import { getUrlParams } from 'utilities/global';
import history from 'shared/services/history';
import {
  ACT_CODE,
  CLOSE_CODE,
  CONDITIONAL_FIELDS_PATHS,
  CONDITIONAL_FIELDS_TYPES,
  DECLINED_STATUS,
  IN_PROGRESS_STATUS,
  MANDATORY_REQUIRED_FIELDS_CARD,
  PAYMENT_METHOD_TYPE,
  REQUIRED_FIELDS,
  TERMS_AND_CONDITIONS,
  THANK_YOU_TITLE_DESKTOP,
  TABS_SELECT_TEXT,
  TYPES_DROPDOWN_SINGLE_PMT_TITLE,
  PMT_FIELD_NAME,
  MANDATORY_CARD_FIELDS,
  SAVE_FOR_FUTURE,
  ADDRESS_FIELDS_TYPE,
  CATEGORIES,
  FIELD_NAMES,
  RECURRENCE_TYPES,
  SET_AS_DEFAULT
} from 'consts';
import { CUSTOMER_FIELDS, CUSTOMER_PREFIX } from 'consts/customer';
import { RequiredFieldsLocation } from 'models/requiredFields';

export const sharePdf = async blob => {
  let sharingFailed = false;

  try {
    if (navigator?.share) {
      const pdfFile = new File([blob], 'payment_information.pdf', {
        type: 'application/pdf'
      });
      await navigator.share({
        title: 'Share Payment Information',
        text: 'Payment Information',
        files: [pdfFile]
      });
    } else {
      sharingFailed = true;
    }
  } catch (error) {
    sharingFailed = true;
  }

  if (sharingFailed) {
    downloadPdf(blob);
  }
};

export const downloadPdf = async blob => {
  if (!blob) return;

  const filename = 'PaymentInformation.pdf';

  if (window.navigator.msSaveOrOpenBlob) {
    // For IE and Edge
    window.navigator.msSaveBlob(blob, filename);
  } else {
    // For other browsers
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  }
};

export const getPageTitle = (category, paymentStatus, blocked, isThereInstructions) => {
  if (blocked) return `thankYouTexts.titles.${THANK_YOU_TITLE_DESKTOP.blocked}`;
  if (!category || !paymentStatus) return THANK_YOU_TITLE_DESKTOP.default;
  let title = '';
  title = THANK_YOU_TITLE_DESKTOP[paymentStatus]?.[category];
  if (isThereInstructions && paymentStatus === ACT_CODE) {
    title = THANK_YOU_TITLE_DESKTOP.actPaymentWithInstructions[category];
  }
  return title ? `thankYouTexts.titles.${title}` : '';
};

const formatPaymentMethodTypeFields = ({ fields, excludeArray = [], location }) => {
  const formattedFields = [];

  const processExclusions = (field, nestedExcludes) => {
    if (!nestedExcludes.includes(field.name)) {
      const formattedField = {
        ...field,
        value: '',
        location,
        error: ''
      };

      if (field.required_fields) {
        formattedField.required_fields = field.required_fields.filter(
          nestedField => !nestedExcludes.includes(nestedField.name)
        );
      }

      formattedFields.push(formattedField);
    }
  };

  forEach(fields, field => {
    if (
      !excludeArray.some(item => {
        if (typeof item === 'string') {
          return item === field.name;
        } else if (typeof item === 'object') {
          const key = Object.keys(item)[0];
          if (key === field.name) {
            processExclusions(field, item[key]);
            return true;
          }
        }
        return false;
      })
    ) {
      formattedFields.push({
        ...field,
        value: '',
        location,
        error: ''
      });
    }
  });

  return formattedFields;
};

const filterRequiredOnly = fields => {
  const formattedFields = {};

  fields.forEach(field => {
    if (field?.is_required) {
      if (field.required_fields)
        formattedFields[field.name] = filterRequiredOnly(field.required_fields);
      else if (field.name === REQUIRED_FIELDS.CUSTOMER) {
        // handle the case we get the old customer object
        return {};
      } else {
        formattedFields[field.name] = field;
      }
    }
  });

  if (formattedFields[CUSTOMER_FIELDS.addresses]) {
    delete formattedFields[CUSTOMER_FIELDS.addresses]?.name;
  }

  return formattedFields;
};

const getUniqueListBy = (arr, key) => {
  return [...new Map(arr.map(item => [item[key], item])).values()];
};

export const getAllCardsRequiredFields = ({ pmts }) => {
  const cardPMTS =
    (pmts || []).find(pmt => pmt.category === PAYMENT_METHOD_TYPE.CARD) || {};
  const cardTypes = cardPMTS?.types || [];
  const allCardsPaymentMethodOptions = cardTypes
    .map(type => type.payment_method_options || [])
    .flat();
  const allCardsFields = cardTypes.map(type => type.fields || []).flat();
  const allPaymentMethodTypesOptions = formatPaymentMethodTypeFields({
    fields: getUniqueListBy(allCardsPaymentMethodOptions, 'name'),
    location: REQUIRED_FIELDS.PAYMENT_METHOD_OPTIONS
  });
  const allPaymentMethodTypesFieldsNoMandatory = formatPaymentMethodTypeFields({
    fields: getUniqueListBy(allCardsFields, 'name'),
    excludeArray: [...MANDATORY_REQUIRED_FIELDS_CARD],
    location: REQUIRED_FIELDS.FIELDS
  });
  return filterRequiredOnly([
    ...allPaymentMethodTypesOptions,
    ...allPaymentMethodTypesFieldsNoMandatory
  ]);
};

export const getCardRequiredFields = ({
  paymentMethodType,
  merchantFields,
  isExistingCard = false
}) => {
  const { excludedRequiredFields, excludedPaymentMethodOptions } = getExcludedPmtFields({
    paymentMethodType: paymentMethodType.type,
    merchantFields
  });
  const paymentMethodOptions = formatPaymentMethodTypeFields({
    fields: paymentMethodType.payment_method_options,
    excludeArray: excludedPaymentMethodOptions,
    location: REQUIRED_FIELDS.PAYMENT_METHOD_OPTIONS
  });

  let fields = [];
  if (!isExistingCard) {
    fields = formatPaymentMethodTypeFields({
      fields: paymentMethodType.fields,
      excludeArray: [...MANDATORY_REQUIRED_FIELDS_CARD, ...excludedRequiredFields],
      location: REQUIRED_FIELDS.FIELDS
    });
  }

  return filterRequiredOnly([...fields, ...paymentMethodOptions]);
};

export const getMerchantPassedFields = ({ paymentMethodType, merchantFields }) => {
  const relatedPmtMerchantFields = merchantFields?.[paymentMethodType];

  return {
    merchantPassedRequiredFields: relatedPmtMerchantFields?.required_fields || {},
    merchantPassedPaymentMethodOptions:
      relatedPmtMerchantFields?.payment_method_options || {},
    merchantPassedPaymentOptions: relatedPmtMerchantFields?.payment_options || {}
  };
};

export const getExcludedPmtFields = ({ paymentMethodType, merchantFields }) => {
  const relatedPmtMerchantFields = merchantFields?.[paymentMethodType];

  if (isEmpty(relatedPmtMerchantFields)) {
    return {
      excludedRequiredFields: [],
      excludedPaymentMethodOptions: [],
      excludedPaymentOptions: []
    };
  }

  const excludedRequiredFields = Object.keys(
    relatedPmtMerchantFields?.required_fields || {}
  );
  const excludedPaymentMethodOptions = Object.keys(
    relatedPmtMerchantFields?.payment_method_options || {}
  );
  const excludedPaymentOptions = [];
  if (
    relatedPmtMerchantFields?.payment_options &&
    typeof relatedPmtMerchantFields.payment_options === 'object'
  ) {
    for (const [key, value] of Object.entries(relatedPmtMerchantFields.payment_options)) {
      if (typeof value === 'object') {
        excludedPaymentOptions.push({ [key]: Object.keys(value) });
      } else {
        excludedPaymentOptions.push(key);
      }
    }
  }

  return {
    excludedRequiredFields,
    excludedPaymentMethodOptions,
    excludedPaymentOptions
  };
};

export const getRequiredFieldsNew = ({
  paymentMethodType,
  merchantFields,
  fieldsToExclude
}) => {
  const {
    type: pmType = '',
    fields = [],
    payment_method_options: paymentMethodOptions = [],
    payment_options: paymentOptions = []
  } = paymentMethodType;

  const { excludedRequiredFields, excludedPaymentMethodOptions, excludedPaymentOptions } =
    getExcludedPmtFields({
      paymentMethodType: pmType,
      merchantFields
    });

  const formattedFields = formatPaymentMethodTypeFields({
    fields,
    excludeArray: [
      ...(fieldsToExclude?.[RequiredFieldsLocation.fields] || []),
      ...excludedRequiredFields
    ],
    location: REQUIRED_FIELDS.FIELDS
  });

  const formattedPaymentMethodOptions = formatPaymentMethodTypeFields({
    fields: paymentMethodOptions,
    excludeArray: excludedPaymentMethodOptions,
    location: REQUIRED_FIELDS.PAYMENT_METHOD_OPTIONS
  });

  const formattedPaymentOptions = formatPaymentMethodTypeFields({
    fields: paymentOptions,
    excludeArray: excludedPaymentOptions,
    location: REQUIRED_FIELDS.PAYMENT_OPTIONS
  });

  return {
    fields: filterRequiredOnly(formattedFields),
    paymentMethodOptions: filterRequiredOnly(formattedPaymentMethodOptions),
    customerRequiredFields: filterRequiredOnly(formattedPaymentOptions)?.customer || {}
  };
};

// Do not use this one
export const getRequiredFields = ({
  paymentMethodType,
  merchantFields,
  asArray = false
}) => {
  const {
    type: pmType = '',
    fields: paymentMethodTypeFields = [],
    payment_method_options: paymentMethodOptions = []
  } = paymentMethodType;

  const { excludedRequiredFields, excludedPaymentMethodOptions } = getExcludedPmtFields({
    paymentMethodType: pmType,
    merchantFields
  });

  const fields = formatPaymentMethodTypeFields({
    fields: paymentMethodTypeFields,
    excludeArray: excludedRequiredFields,
    location: REQUIRED_FIELDS.FIELDS
  });

  const formattedPaymentMethodOptions = formatPaymentMethodTypeFields({
    fields: paymentMethodOptions,
    excludeArray: excludedPaymentMethodOptions,
    location: REQUIRED_FIELDS.PAYMENT_METHOD_OPTIONS
  });

  if (asArray)
    return Object.values(filterRequiredOnly([...fields, ...paymentMethodOptions]));

  return {
    formattedFields: filterRequiredOnly(fields),
    options: filterRequiredOnly(formattedPaymentMethodOptions)
  };
};

export const findPaymentMethod = (paymentMethodTypes, type, category) => {
  const categories = findLast(paymentMethodTypes, item => item.category === category);
  if (!categories) return {};

  const typeObj = findLast(categories.types, item => item.type === type);
  return typeObj;
};

export const getInstructions = instructionsArray => {
  let instructionObj;
  if (Array.isArray(instructionsArray)) {
    instructionObj = head(instructionsArray);
  }
  if (isPlainObject(instructionsArray)) {
    instructionObj = instructionsArray;
  }

  if (!isPlainObject(instructionObj) || !Array.isArray(instructionObj.steps)) return [];

  const flattenedSteps = map(instructionObj.steps, step => {
    if (typeof step === 'string') return step;

    if (isPlainObject(step)) {
      const stepValues = values(step);
      if (stepValues.length === 1 && stepValues[0]?.includes('\n\t')) {
        return stepValues[0].split('\n\t');
      }
      return stepValues;
    }

    return '';
  }).flat();

  const compacted = flattenedSteps.reduce((result, step) => {
    if (typeof step === 'string' && step !== '') {
      return [...result, step];
    }
    return [...result];
  }, []);

  return compacted;
};

export const getTermsAndConditions = instructionsArray => {
  const instruction = head(instructionsArray) ?? instructionsArray;
  if (isEmpty(instruction)) return '';

  return instruction[TERMS_AND_CONDITIONS] || '';
};

export const createAndSubmitForm = ({ data, url, method = 'GET', target = '_self' }) => {
  if (!url) return;

  const form = document.createElement('form');
  form.method = method;
  form.action = url;
  form.target = target;

  Object.entries(data).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'text';
    input.name = key;
    input.value = value;

    form.appendChild(input);
  });

  form.style.visibility = 'hidden';
  document.body.append(form);
  form.submit();
};

export const submitKortaForm = ({
  kwpAdditionalData,
  completeCheckoutUrl,
  isSuccess = true
}) => {
  const {
    auth_code: authcode = '',
    card_last4: card4 = '',
    card_brand: cardbrand = '',
    payment_updated_at: paymentUpdatedAt = '',
    payment_paid_at: paymentPaidAt = '',
    payment_reference: reference = '',
    check_value_md5: checkvaluemd5 = '',
    download_md5: downloadmd5 = '',
    transaction_id: transactionId = '',
    download_url: url = '',
    refer_method: method,
    refer_target: target
  } = kwpAdditionalData || {};

  if (!['GET', 'POST'].includes(method) || !isSuccess) {
    if (completeCheckoutUrl) {
      window.location.href = completeCheckoutUrl;
    }
    return !!completeCheckoutUrl;
  }

  const urlParams = getUrlParams(url);
  const time = paymentPaidAt || paymentUpdatedAt;
  createAndSubmitForm({
    data: {
      ...urlParams,
      authcode,
      card4,
      cardbrand,
      time,
      reference,
      checkvaluemd5,
      downloadmd5,
      transactionId
    },
    method,
    url,
    target
  });

  return !!url;
};

export const isAnotherPaymentAllowed = ({
  paymentsCreated,
  maxPayments,
  userAgent,
  currentUserAgent = navigator.userAgent
}) => {
  return paymentsCreated < maxPayments && userAgent === currentUserAgent;
};

export const shouldRedirectToThankYou = ({
  pageStatus,
  payment,
  paymentsMetadata,
  maxPayments
}) => {
  const { status } = payment || {};
  const { payments_created: paymentsCreated, user_agent: userAgent } =
    paymentsMetadata || {};

  if (pageStatus === DECLINED_STATUS) return true;
  if ([ACT_CODE, CLOSE_CODE].includes(status)) return true;

  const isAllowAnotherPayment = isAnotherPaymentAllowed({
    paymentsCreated,
    maxPayments,
    userAgent
  });
  return isFailedPayment(status) && !isAllowAnotherPayment;
};

export const checkIsCompletedPayment = checkoutPage => {
  const { payment } = checkoutPage || {};
  const { id: paymentId, status } = payment || {};
  const isCompletedPayment = paymentId && !isFailedPayment(status);

  return isCompletedPayment;
};

export const checkIsInProgressPayment = checkoutPage => {
  const { status: pageStatus } = checkoutPage || {};
  const isInProgressPayment = pageStatus === IN_PROGRESS_STATUS;

  return isInProgressPayment;
};

export const getPaymentMethodTypesByCategory = (aggregatedPmts, categoryToFilter) => {
  return (
    aggregatedPmts?.find(({ category }) => category === categoryToFilter)?.types || []
  );
};

export const getCheckoutHeaderPropsDesktop = ({ redirectUrl, isFromReusableLink }) => {
  const headerProps = {
    redirectUrl,
    withModal: true
  };
  const reusableOverrideHeaderProps = {
    withModal: false,
    hideBackText: true,
    backCallback: () => history.goBack()
  };

  return {
    ...headerProps,
    ...(isFromReusableLink && reusableOverrideHeaderProps)
  };
};

export const calcCurrencySelectionPaymentMethodTypeCurrencies = ({
  cardNumber,
  cardPaymentMethodTypes
}) => {
  const { type: currentInputBrand } = parseCardBrand(cardNumber);
  if (!currentInputBrand) return [];

  return cardPaymentMethodTypes.reduce((supported, pmt) => {
    if (!pmt.mapping?.length) return [...supported];

    const foundBrandMapping = pmt.mapping.find(
      brandMapping => brandMapping.brand === currentInputBrand
    );
    if (!foundBrandMapping) return [...supported];

    const { supported_currencies: supportedPMTCurrencies } = pmt;
    return Array.from(new Set([...supported, ...supportedPMTCurrencies]));
  }, []);
};

export const getCustomerDefaultPmtCategory = ({
  savedCards,
  savedBankRedirect,
  categories
}) => {
  const categoriesArray = categories.map(category => category.category);
  if (
    savedBankRedirect?.find(pm => pm.default) &&
    categoriesArray.includes(PAYMENT_METHOD_TYPE.BANK)
  ) {
    return PAYMENT_METHOD_TYPE.BANK;
  }
  if (savedCards?.length && categoriesArray.includes(PAYMENT_METHOD_TYPE.CARD)) {
    return PAYMENT_METHOD_TYPE.CARD;
  }
  return null;
};

const setSourceConditionalFields = ({ pmtFields = [], conditionalFields = {} }) => {
  pmtFields.forEach(field => {
    if (conditionalFields[field.name]) field.hasCondition = true;
  });
};

export const getConditionalFieldsMapping = ({ allFields = [] }) => {
  const conditionalFields = {};

  allFields.forEach(field => {
    if (field?.conditions?.length) {
      field.conditions.forEach(condition => {
        const sourceFieldNamePath = condition?.element_name || '';

        if (!sourceFieldNamePath.startsWith(CONDITIONAL_FIELDS_PATHS.payment)) return;

        const sourceFieldName = sourceFieldNamePath.split('.').pop();
        const targetFieldName = field.name;
        const formattedCondition = {
          source: sourceFieldName,
          operator: condition.operator || '',
          thresholdValue: condition.threshold_value || '',
          target: targetFieldName
        };

        const conditionType = getConditionalFieldConditionType({ sourceFieldNamePath });

        if (!conditionalFields[conditionType]) {
          conditionalFields[conditionType] = {};
        }

        if (!conditionalFields[conditionType][sourceFieldName]) {
          conditionalFields[conditionType][sourceFieldName] = [];
        }

        conditionalFields[conditionType][sourceFieldName].push(formattedCondition);
      });
    }
  });

  if (Object.keys(conditionalFields?.paymentMethodType || {})?.length) {
    setSourceConditionalFields({
      pmtFields: allFields,
      conditionalFields: conditionalFields.paymentMethodType
    });
  }

  return conditionalFields;
};

const getConditionalFieldConditionType = ({ sourceFieldNamePath = '' }) => {
  const isOrganizationField = sourceFieldNamePath.startsWith(
    CONDITIONAL_FIELDS_PATHS.organization
  );
  const isPaymentField = sourceFieldNamePath.startsWith(CONDITIONAL_FIELDS_PATHS.payment);
  const isPaymentMethodTypeField =
    isPaymentField &&
    sourceFieldNamePath.includes(CONDITIONAL_FIELDS_PATHS.paymentMethodType);

  return isOrganizationField
    ? CONDITIONAL_FIELDS_TYPES.organization
    : isPaymentMethodTypeField
    ? CONDITIONAL_FIELDS_TYPES.paymentMethodType
    : isPaymentField
    ? CONDITIONAL_FIELDS_TYPES.payment
    : '';
};

export const requiredFieldsModifier = ({
  requiredFields = {},
  fieldsToAdd = {},
  fieldsToRemove = []
}) => {
  if (!Object.keys(fieldsToAdd)?.length && !fieldsToRemove?.length) return requiredFields;

  const requiredFieldsClone = cloneDeep(requiredFields);

  if (fieldsToRemove?.length) {
    fieldsToRemove.forEach(fieldName => delete requiredFieldsClone[fieldName]);
  }

  return {
    ...requiredFieldsClone,
    ...fieldsToAdd
  };
};

export const getFieldOperator = ({ operator = '' }) => {
  const operators = {
    $eq: (value, threshold) => {
      if (isString(value) && isString(threshold)) {
        return value === threshold;
      }

      if (isNumber(value) && isString(threshold)) {
        return value.toString() === threshold;
      }
      return false;
    },
    $ne: (value, threshold) => {
      if (isString(value) && isString(threshold)) {
        return value !== threshold;
      }

      if (isNumber(value) && isString(threshold)) {
        return value.toString() !== threshold;
      }
      return false;
    },
    $gt: (value, threshold) => {
      if (value && threshold) {
        const valueToNumber = Number(value);
        const thresholdToNumber = Number(threshold);

        if (valueToNumber && thresholdToNumber) {
          return valueToNumber > thresholdToNumber;
        }

        return false;
      }
      return false;
    },
    $lt: (value, threshold) => {
      if (value && threshold) {
        const valueToNumber = Number(value);
        const thresholdToNumber = Number(threshold);

        if (valueToNumber && thresholdToNumber) {
          return valueToNumber < thresholdToNumber;
        }

        return false;
      }
      return false;
    },
    $gte: (value, threshold) => {
      if (value && threshold) {
        const valueToNumber = Number(value);
        const thresholdToNumber = Number(threshold);

        if (valueToNumber && thresholdToNumber) {
          return valueToNumber >= thresholdToNumber;
        }

        return false;
      }

      return false;
    },
    $lte: (value, threshold) => {
      if (value && threshold) {
        const valueToNumber = Number(value);
        const thresholdToNumber = Number(threshold);

        if (valueToNumber && thresholdToNumber) {
          return valueToNumber <= thresholdToNumber;
        }

        return false;
      }
      return false;
    },
    $in: (value, threshold) => {
      if (value && Array.isArray(threshold)) {
        return threshold.includes(value);
      }

      return false;
    },
    $nin: (value, threshold) => {
      if (value && Array.isArray(threshold)) {
        return !threshold.includes(value);
      }

      return false;
    }
  };

  return operators[operator];
};

export const testConditionalFields = ({
  conditionalFields = {},
  requiredFields = {},
  payment = {},
  paymentMethodTypeFields = [],
  fieldKey = '',
  fieldValue = '',
  conditionsType = ''
}) => {
  const conditionalFieldsToTest = conditionalFields?.[conditionsType] || {};
  const isPaymentConditional = conditionsType === CONDITIONAL_FIELDS_TYPES.payment;
  const result = {
    fieldsToAdd: {},
    fieldsToRemove: []
  };

  if (!Object.keys(conditionalFieldsToTest)?.length) return result;

  Object.entries(conditionalFieldsToTest).forEach(
    ([sourceFieldName, sourceFieldsConditions]) => {
      let isConditionPass = true;
      let targetFieldName;

      if (!isPaymentConditional && fieldKey !== sourceFieldName) return;

      sourceFieldsConditions.forEach(condition => {
        const { operator, thresholdValue, source, target } = condition;
        targetFieldName = target;

        const operatorFunc = getFieldOperator({ operator });
        const valueToTest = isPaymentConditional ? payment?.[source] : fieldValue;

        isConditionPass = isConditionPass && operatorFunc?.(valueToTest, thresholdValue);
      });

      if (isConditionPass) {
        if (!requiredFields?.[targetFieldName]) {
          const targetField =
            paymentMethodTypeFields?.find(pmt => pmt?.name === targetFieldName) || {};

          const fieldClone = cloneDeep(targetField);
          fieldClone.is_required = true;

          const [formattedField] = formatPaymentMethodTypeFields({
            fields: [fieldClone],
            location: REQUIRED_FIELDS.FIELDS
          });

          result.fieldsToAdd[targetFieldName] = formattedField;
        }
      } else if (requiredFields?.[targetFieldName]) {
        result.fieldsToRemove.push(targetFieldName);
      }
    }
  );

  return result;
};

export const getPmtsDropdownTitle = ({ isOneOption, category }) => {
  const title = isOneOption
    ? TYPES_DROPDOWN_SINGLE_PMT_TITLE
    : TABS_SELECT_TEXT[category];
  return title;
};

export const getPmImagesSrc = ({ pmImages, category, isMobile }) => {
  if (!category) return;
  const view = isMobile ? 'mobile' : 'desktop';
  return pmImages?.[category.toLowerCase()]?.[view];
};

export const parseFormValuesDispatch = ({
  formValues,
  pmts,
  paymentMethod = {},
  customerData
}) => {
  const pmtType = paymentMethod?.type || formValues[PMT_FIELD_NAME];
  const selectedPmt = pmts.find(pmt => pmt.type === pmtType);
  const setCardAsDefault = formValues[SET_AS_DEFAULT];
  let parsedCustomerFields;
  if (!isEmpty(formValues[CUSTOMER_PREFIX])) {
    parsedCustomerFields = parseCustomerToRequest({
      customerData,
      values: formValues[CUSTOMER_PREFIX]
    });
  }

  return {
    paymentMethodType: selectedPmt,
    paymentMethod: paymentMethod?.id || null,
    fields: formValues[RequiredFieldsLocation.fields] || {},
    pmOptions: formValues[RequiredFieldsLocation.payment_method_options] || {},
    setCardAsDefault: setCardAsDefault,
    customerFields: parsedCustomerFields
  };
};

export const parseCardValuesDispatch = ({
  formValues,
  pmt,
  requiredFields,
  customerFields,
  shouldSendBillingAddress,
  isAftFlow = false,
  recurrenceType = RECURRENCE_TYPES.unscheduled
}) => {
  // in case of card redirect we ignore all the values,
  const { is_redirect: isCardRedirect, fields: pmtFields } = pmt || {};
  if (isCardRedirect) return {};

  let paymentMethodAddress = {};
  let mergedCustomerFields = customerFields;
  const { [CUSTOMER_FIELDS.addresses]: customerFieldsAddresses } = customerFields || {};
  const customerFieldsAddress = customerFieldsAddresses?.[0] || [];

  let cardFields = {
    number: formValues[MANDATORY_CARD_FIELDS.number]?.replace(/ /g, ''),
    cvv: formValues[MANDATORY_CARD_FIELDS.cvv],
    expiration_month: formValues[MANDATORY_CARD_FIELDS.expiration]?.substring(0, 2),
    expiration_year: formValues[MANDATORY_CARD_FIELDS.expiration]?.substr(-2),
    name: formValues[MANDATORY_CARD_FIELDS.cardholder]
  };

  const saveForFuture = !!formValues[SAVE_FOR_FUTURE];
  if (saveForFuture) {
    paymentMethodAddress =
      !isAftFlow && shouldSendBillingAddress
        ? merge({}, formValues[ADDRESS_FIELDS_TYPE.billing_address], {
            [CUSTOMER_FIELDS.name]: cardFields?.name
          })
        : {};

    const pmtFieldsIncludeRecurrenceType = pmtFields?.some(
      field => field.name === FIELD_NAMES.recurrence_type
    );

    if (pmtFieldsIncludeRecurrenceType) {
      cardFields = merge({}, cardFields, {
        recurrence_type: recurrenceType
      });
    }
  }
  const mergedFields = merge({}, requiredFields || {}, cardFields || {});

  const isCustomerAddressValid =
    !isEmpty(customerFieldsAddress) &&
    CUSTOMER_FIELDS.name in customerFieldsAddress &&
    CUSTOMER_FIELDS.line_1 in customerFieldsAddress;
  const customerAddress = isCustomerAddressValid
    ? {
        [CUSTOMER_FIELDS.addresses]: [customerFieldsAddress]
      }
    : {};

  mergedCustomerFields = isAftFlow
    ? null
    : merge({}, customerFields || {}, customerAddress || {});

  return {
    cardFields: mergedFields,
    saveForFuture,
    paymentMethodAddress,
    mergedCustomerFields
  };
};

const isMobileMoneyPmtExist = pmts => {
  const ewalletPmts = find(pmts, item => item.category === CATEGORIES.ewallet)?.types;
  return some(ewalletPmts, pmt => pmt.is_mobile_money);
};

export const shouldChangeEwalletToMobileMoney = (categoryName, paymentMethodTypes) => {
  let pmtCategoryName = categoryName;

  if (categoryName === CATEGORIES.ewallet) {
    if (isMobileMoneyPmtExist(paymentMethodTypes)) {
      pmtCategoryName = 'mobile_money';
    }
  }

  return pmtCategoryName;
};
